import { motion } from "framer-motion";
import { Check, ArrowUpRight } from "lucide-react";
import { SectionHeading, EASE } from "./motionPrimitives";
import { scrollToHash } from "./Navbar";

const PLANS = [
    {
        id: "pricing-card-launch",
        name: "Launch",
        price: "$1,450",
        period: "/month",
        blurb: "For businesses getting the foundation right.",
        features: [
            "One core channel — SEO or paid media",
            "Monthly strategy call",
            "Monthly reporting dashboard",
            "Email support",
        ],
        featured: false,
        cta: "Start with Launch",
    },
    {
        id: "pricing-card-growth",
        name: "Growth",
        price: "$3,200",
        period: "/month",
        blurb: "Our most popular engine for scaling companies.",
        features: [
            "Up to three channels, fully managed",
            "Landing pages & ad creative included",
            "One AI agent or automation built in",
            "Bi-weekly growth sprints",
            "Priority support",
        ],
        featured: true,
        cta: "Scale with Growth",
    },
    {
        id: "pricing-card-dominate",
        name: "Dominate",
        price: "Custom",
        period: "",
        blurb: "Full-funnel partnership for market leaders.",
        features: [
            "All four service pillars, one team",
            "Dedicated growth squad",
            "Custom AI systems & CRM automation",
            "Weekly sprints & quarterly roadmaps",
        ],
        featured: false,
        cta: "Request a proposal",
    },
];

export default function Pricing() {
    return (
        <section id="pricing" className="bg-fog py-20 lg:py-28">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <SectionHeading
                    eyebrow="Pricing"
                    title="Straightforward plans, serious results."
                    className="max-w-2xl"
                />

                <div className="mt-14 grid gap-6 lg:grid-cols-3 items-stretch">
                    {PLANS.map((p, i) => (
                        <motion.div
                            key={p.id}
                            data-testid={p.id}
                            initial={{ opacity: 0, y: 36 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.2 }}
                            transition={{ duration: 0.7, delay: i * 0.1, ease: EASE }}
                            className={`relative flex flex-col rounded-3xl p-8 transition-transform duration-300 hover:-translate-y-1 ${
                                p.featured
                                    ? "bg-navy text-white border-2 border-gold shadow-2xl shadow-navy/30 lg:-translate-y-4"
                                    : "bg-white border border-[#E2E8F0] hover:border-gold/60"
                            }`}
                        >
                            {p.featured && (
                                <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-gold text-navy font-mono text-[11px] font-semibold uppercase tracking-widest px-4 py-1.5 rounded-full whitespace-nowrap">
                                    Most popular
                                </span>
                            )}
                            <h3 className="font-mono text-xs uppercase tracking-[0.3em] font-semibold text-gold">
                                {p.name}
                            </h3>
                            <p className="mt-4 flex items-baseline gap-1">
                                <span className="font-display text-4xl sm:text-5xl font-extrabold">
                                    {p.price}
                                </span>
                                <span
                                    className={`text-sm ${p.featured ? "text-mist" : "text-steel"}`}
                                >
                                    {p.period}
                                </span>
                            </p>
                            <p
                                className={`mt-3 text-sm leading-relaxed ${
                                    p.featured ? "text-mist" : "text-steel"
                                }`}
                            >
                                {p.blurb}
                            </p>
                            <ul className="mt-7 space-y-3 flex-1">
                                {p.features.map((f) => (
                                    <li key={f} className="flex items-start gap-3 text-sm">
                                        <span className="mt-0.5 inline-flex items-center justify-center w-5 h-5 rounded-full bg-gold/15 shrink-0">
                                            <Check size={12} className="text-gold" />
                                        </span>
                                        <span className={p.featured ? "text-white/90" : "text-navy"}>
                                            {f}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                            <button
                                data-testid={`${p.id}-cta`}
                                onClick={() => scrollToHash("#contact")}
                                className={`mt-8 inline-flex items-center justify-center gap-2 rounded-full py-3.5 font-bold transition-colors ${
                                    p.featured
                                        ? "btn-shine bg-gold text-navy hover:bg-gold-hover"
                                        : "border-2 border-navy text-navy hover:bg-navy hover:text-white"
                                }`}
                            >
                                {p.cta}
                                <ArrowUpRight size={17} />
                            </button>
                        </motion.div>
                    ))}
                </div>

                <p className="mt-10 text-center font-mono text-[11px] uppercase tracking-[0.2em] text-steel/70">
                    Guide pricing only — every engagement is scoped to your goals and market
                </p>
            </div>
        </section>
    );
}
